    import { useCallback } from 'react';
    import usePDA1100_R01Store from "./usePDA1100_R01Store";
    import { scanPDA1100_R01, updatePDA1100_R01 } from "./pda1100Api";
    import { useToast } from "src/hooks/useToast";

    /**
     * PDA1100_R01 Content Hook
     * 
     * 비즈니스 로직:
     * - 스캔 → 품목 정보 조회
     * - 저장위치 수정
     */
    export const usePDA1100_R01Content = () => {
    const { 
        scanValue,
        setScanValue,
        newLocationCd,
        setNewLocationCd,
        data,
        setData,
        setLoading,
        resetForm,
    } = usePDA1100_R01Store();
    
    const { showToast } = useToast();

    /**
     * 스캔 조회
     */
    const handleScan = useCallback(async () => {
        if (!scanValue.trim()) {
        showToast("error", "스캔값을 입력하세요.");
        return;
        }

        try {
        setLoading(true);
        
        const response = await scanPDA1100_R01(scanValue.trim());
        const itemData = response?.data?.data;
        
        if (!itemData) {
            throw new Error("품목 정보를 찾을 수 없습니다.");
        }
        
        setData(itemData);
        setScanValue(""); // 스캔 입력 초기화
        setNewLocationCd(""); // 새 저장위치 초기화
        showToast("success", `스캔 완료: ${itemData.itemNm}`);
        
        } catch (error: any) {
        console.error("[PDA1100_R01] 스캔 실패:", error);
        showToast("error", error?.response?.data?.message || "스캔에 실패했습니다.");
        setData(null);
        } finally {
        setLoading(false);
        }
    }, [scanValue, setLoading, setData, setScanValue, setNewLocationCd, showToast]);

    /**
     * 저장위치 수정
     */
    const handleSave = useCallback(async () => {
        // 유효성 검증
        if (!data) {
        showToast("error", "품목을 먼저 스캔하세요.");
        return;
        }

        if (!newLocationCd.trim()) {
        showToast("error", "새 저장위치를 입력하세요.");
        return;
        }

        if (newLocationCd.trim() === data.locationCd) {
        showToast("warning", "현재 저장위치와 동일합니다.");
        return;
        }

        try {
        setLoading(true);

        const response = await updatePDA1100_R01({
            itemCd: data.itemCd,
            newLocationCd: newLocationCd.trim(),
        });

        const result = response?.data?.data;

        if (result) {
            showToast(
            "success", 
            `저장위치 변경 완료: ${data.locationCd} → ${result.locationCd}`
            );
            resetForm(); // 폼 초기화
        }

        } catch (error: any) {
        console.error("[PDA1100_R01] 저장 실패:", error);
        showToast("error", error?.response?.data?.message || "저장에 실패했습니다.");
        } finally {
        setLoading(false);
        }
    }, [data, newLocationCd, setLoading, showToast, resetForm]);

    /**
     * 취소 (입력 초기화)
     */
    const handleCancel = useCallback(() => {
        resetForm();
        showToast("info", "입력이 초기화되었습니다.");
    }, [resetForm, showToast]);

    /**
     * Enter 키 핸들러
     */
    const handleScanEnter = useCallback(() => {
        handleScan();
    }, [handleScan]);

    const handleLocationEnter = useCallback(() => {
        handleSave();
    }, [handleSave]);

    return {
        handleScan,
        handleSave,
        handleCancel,
        handleScanEnter,
        handleLocationEnter,
    };
    };
